import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaMusic } from 'react-icons/fa';
import axios from 'axios';
import '../styles/BookingForm.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function DJBookingForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    email: '',
    telefon: '',
    eventTyp: '',
    eventDatum: '',
    startzeit: '',
    eventOrt: '',
    gaeste: '',
    basisDauer: '4',
    extraStunden: '0',
    partyStil: '',
    notizen: '',
    status: 'Neu',
    manuellerPreis: ''
  });

  useEffect(() => {
    if (isEdit) {
      loadBooking();
    }
  }, [id]);

  const loadBooking = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/dj/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const booking = response.data.booking;
      
      setFormData({
        vorname: booking.Vorname || '',
        nachname: booking.Nachname || '',
        email: booking['E-Mail'] || '',
        telefon: booking.Telefon || '',
        eventTyp: booking['Event-Typ']?.[0] || '', // Erstes Element aus Array nehmen
        eventDatum: booking['Event-Datum'] ? booking['Event-Datum'].split('T')[0] : '',
        startzeit: booking.Startzeit || '',
        eventOrt: booking['Event Ort'] || '',
        gaeste: booking.Gästeanzahl || '',
        basisDauer: booking['Basis-Dauer'] || '4',
        extraStunden: booking['Extra-Stunden'] || '0',
        partyStil: booking['Party-Stil'] || '',
        notizen: booking['Event-Notizen'] || '',
        status: booking.Buchungsstatus || 'Neu',
        manuellerPreis: booking['Manueller Preis aus CRM'] || ''
      });
    } catch (error) {
      console.error('Error loading DJ booking:', error);
      alert('Fehler beim Laden der DJ-Buchung');
      navigate('/dj');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      if (isEdit) {
        await axios.put(`${API_URL}/dj/${id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/dj`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      navigate('/dj');
    } catch (error) {
      console.error('Error saving DJ booking:', error);
      alert('Fehler beim Speichern: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="booking-form-page">
      <div className="page-header">
        <div>
          <Link to={isEdit ? `/dj/${id}` : '/dj'} className="back-button">
            <FaArrowLeft /> Zurück
          </Link>
          <h1><FaMusic /> {isEdit ? 'DJ-Buchung bearbeiten' : 'Neue DJ-Buchung'}</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="booking-form">
        <div className="card">
          <h2>Kundendaten</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="vorname">Vorname *</label>
              <input
                type="text"
                id="vorname"
                name="vorname"
                className="form-input"
                value={formData.vorname}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="nachname">Nachname *</label>
              <input
                type="text"
                id="nachname"
                name="nachname"
                className="form-input"
                value={formData.nachname}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">E-Mail *</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="telefon">Telefon</label>
              <input
                type="tel"
                id="telefon"
                name="telefon"
                className="form-input"
                value={formData.telefon}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h2>Event-Details</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="eventTyp">Event-Typ</label>
              <select
                id="eventTyp"
                name="eventTyp"
                className="form-select"
                value={formData.eventTyp}
                onChange={handleChange}
              >
                <option value="">Bitte wählen...</option>
                <option value="Hochzeit">Hochzeit</option>
                <option value="Geburtstag">Geburtstag</option>
                <option value="Firmenfeier">Firmenfeier</option>
                <option value="Jubiläum">Jubiläum</option>
                <option value="Abiball">Abiball</option>
                <option value="Clubevent">Clubevent</option>
                <option value="Festival">Festival</option>
                <option value="Privatparty">Privatparty</option>
                <option value="Sonstiges">Sonstiges</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="eventDatum">Event-Datum *</label>
              <input
                type="date"
                id="eventDatum"
                name="eventDatum"
                className="form-input"
                value={formData.eventDatum}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startzeit">Startzeit</label>
              <input
                type="time"
                id="startzeit"
                name="startzeit"
                className="form-input"
                value={formData.startzeit}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="gaeste">Gästeanzahl</label>
              <input
                type="number"
                id="gaeste"
                name="gaeste"
                className="form-input"
                value={formData.gaeste}
                onChange={handleChange}
                min="0"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="eventOrt">Event-Ort</label>
            <input
              type="text"
              id="eventOrt"
              name="eventOrt"
              className="form-input"
              value={formData.eventOrt}
              onChange={handleChange}
              placeholder="z.B. Eventlocation XYZ, Musterstraße 123, 12345 Stadt"
            />
          </div>
        </div>

        <div className="card">
          <h2>Buchungsdetails</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="basisDauer">Basis-Dauer (Stunden)</label>
              <input
                type="number"
                id="basisDauer"
                name="basisDauer"
                className="form-input"
                value={formData.basisDauer}
                onChange={handleChange}
                min="1"
                max="12"
              />
            </div>

            <div className="form-group">
              <label htmlFor="extraStunden">Extra-Stunden</label>
              <input
                type="number"
                id="extraStunden"
                name="extraStunden"
                className="form-input"
                value={formData.extraStunden}
                onChange={handleChange}
                min="0"
                max="8"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="manuellerPreis">Preis (€)</label>
            <input
              type="number"
              id="manuellerPreis"
              name="manuellerPreis"
              className="form-input"
              value={formData.manuellerPreis}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="z.B. 1199.00"
            />
            <small style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: '0.3rem', display: 'block' }}>
              Individueller Preis für diese Buchung
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              className="form-select"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="Neu">Neu</option>
              <option value="In Bearbeitung">In Bearbeitung</option>
              <option value="Angebot erstellt">Angebot erstellt</option>
              <option value="Bestätigt">Bestätigt</option>
              <option value="Abgeschlossen">Abgeschlossen</option>
              <option value="Abgelehnt">Abgelehnt</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="partyStil">Party-Stil / Musikwünsche</label>
            <textarea
              id="partyStil"
              name="partyStil"
              className="form-textarea"
              value={formData.partyStil}
              onChange={handleChange}
              rows="3"
              placeholder="z.B. House, Disco, 80er/90er, Charts, ..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="notizen">Event-Notizen</label>
            <textarea
              id="notizen"
              name="notizen"
              className="form-textarea"
              value={formData.notizen}
              onChange={handleChange}
              rows="4"
            />
          </div>
        </div>

        <div className="form-actions">
          <Link to={isEdit ? `/dj/${id}` : '/dj'} className="btn btn-secondary">
            Abbrechen
          </Link>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            <FaSave />
            {saving ? 'Wird gespeichert...' : (isEdit ? 'Änderungen speichern' : 'DJ-Buchung erstellen')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default DJBookingForm;
